import {isReleasedEntryManagement} from '../../../config/unReleasePage';
import type {GetTodoRemindersReturnValueType} from '../../../core/repositories/entryTodosRepository';
import {useGetTodoReminders} from '../../../core/repositories/entryTodosRepository';
import type {GetRemindersReturnValueType} from '../../../core/repositories/mypageEntryManagementsRepository';
import {useGetReminders} from '../../../core/repositories/mypageEntryManagementsRepository';
import {useAuth} from '../../../core/usecases/auth/AuthContext';

type UseEntryManagementRemindersReturn = {
  hasTodoReminder: boolean;
  hasProgressReminder: boolean;
  hasAnyReminder: boolean;
  totalCount: number;
  isLoading: boolean;
};

/**
 * リマインダー件数を持つオブジェクトの型
 */
type ReminderCounts = Record<string, number | undefined>;

/**
 * API Result型の基本構造
 */
type ApiResult<T> = {
  ok?: boolean;
  value?: T;
};

/**
 * TODOリマインダーの集計対象フィールド
 */
const TODO_REMINDER_FIELDS = [
  'dueWithinThreeDays',
  'dueToday',
  'overdue',
] as const;

/**
 * 選考管理リマインダーの集計対象フィールド
 */
const PROGRESS_REMINDER_FIELDS = [
  'deadlineWithinThreeDays',
  'deadlineWithinDay',
  'startDateWithinWeek',
  'startDateWithinDay',
] as const;

/**
 * リマインダーデータの検証
 * @param reminderData APIレスポンス（Result型）
 * @returns データが有効な場合true
 */
const isValidReminderData = <T>(
  reminderData: ApiResult<T> | undefined,
): reminderData is ApiResult<T> & {ok: true; value: T} => {
  return reminderData?.ok === true && reminderData.value !== undefined;
};

/**
 * 指定されたフィールドのいずれかが1以上の値を持つか判定する
 * @param data リマインダーデータ
 * @param fields チェック対象のフィールド配列
 * @returns いずれかのフィールドが1以上の場合true
 */
const hasAnyFieldWithValue = <T extends ReminderCounts>(
  data: T,
  fields: ReadonlyArray<keyof T>,
): boolean => {
  return fields.some((field) => (data[field] ?? 0) > 0);
};

/**
 * TODOリマインダーが1件以上存在するか判定する
 * @param reminderData APIレスポンス（Result型）
 * @returns いずれかの期限カテゴリにリマインダーが存在する場合true
 */
const hasTodoReminderExists = (
  reminderData: GetTodoRemindersReturnValueType | undefined,
): boolean => {
  if (!isValidReminderData(reminderData)) {
    return false;
  }

  return hasAnyFieldWithValue(reminderData.value, TODO_REMINDER_FIELDS);
};

/**
 * 選考管理リマインダーが1件以上存在するか判定する
 * @param reminderData APIレスポンス（Result型）
 * @returns いずれかの期限カテゴリにリマインダーが存在する場合true
 */
const hasProgressReminderExists = (
  reminderData: GetRemindersReturnValueType | undefined,
): boolean => {
  if (!isValidReminderData(reminderData)) {
    return false;
  }

  return hasAnyFieldWithValue(reminderData.value, PROGRESS_REMINDER_FIELDS);
};

/**
 * 指定されたフィールドの値を合計する
 * @param reminderData APIレスポンス（Result型）
 * @param fields 合計対象のフィールド名配列
 * @returns フィールド値の合計（データがない場合は0）
 */
const sumReminderFields = <T extends ReminderCounts>(
  reminderData: ApiResult<T> | undefined,
  fields: ReadonlyArray<keyof T>,
): number => {
  if (!isValidReminderData(reminderData)) {
    return 0;
  }

  // 各フィールドの値を合計（undefinedの場合は0として扱う）
  return fields.reduce((sum, field) => {
    const value = reminderData.value[field] ?? 0;
    return sum + (typeof value === 'number' ? value : 0);
  }, 0);
};

/**
 * entry-todosとentry-managementsのリマインダー情報を取得し、
 * バッジ表示に必要な情報を返すカスタムフック
 *
 * @returns リマインダーの有無、合計数、読み込み状態
 */
export const useEntryManagementReminders =
  (): UseEntryManagementRemindersReturn => {
    const {status} = useAuth();
    const isAuthenticated = status === 'authenticated';
    const isReleased = isReleasedEntryManagement();

    // 認証済みの場合のみAPIを呼び出す
    const {data: todoReminders, isLoading: isTodoLoading} = useGetTodoReminders(
      {
        config: {
          enabled: isAuthenticated && isReleased,
        },
      },
    );

    const {data: progressReminders, isLoading: isProgressLoading} =
      useGetReminders({
        config: {
          enabled: isAuthenticated && isReleased,
        },
      });

    // 未リリースの場合は早期リターン
    if (!isReleased) {
      return {
        hasTodoReminder: false,
        hasProgressReminder: false,
        hasAnyReminder: false,
        totalCount: 0,
        isLoading: false,
      };
    }

    // リマインダーの存在判定（ヘルパー関数を使用）
    const hasTodoReminder = hasTodoReminderExists(todoReminders);
    const hasProgressReminder = hasProgressReminderExists(progressReminders);

    // いずれかのリマインダーが存在するか
    const hasAnyReminder = hasTodoReminder || hasProgressReminder;

    // リマインダーの合計数を計算（ヘルパー関数と定数を使用）
    const todoCount = sumReminderFields(todoReminders, TODO_REMINDER_FIELDS);
    const progressCount = sumReminderFields(
      progressReminders,
      PROGRESS_REMINDER_FIELDS,
    );

    const totalCount = todoCount + progressCount;

    return {
      hasTodoReminder,
      hasProgressReminder,
      hasAnyReminder,
      totalCount,
      isLoading: isTodoLoading || isProgressLoading,
    };
  };
